import { DeleteIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useDeleteProject } from '@/hooks'
import type { Project } from '@/types'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface DeleteProjectDialogProps {
  project: Project
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteProjectDialog({ project, open, onOpenChange }: DeleteProjectDialogProps) {
  const [confirmationText, setConfirmationText] = useState('')
  const deleteProject = useDeleteProject()
  const navigate = useNavigate()
  const location = useLocation()

  const isConfirmationValid = confirmationText === project.name

  useEffect(() => {
    if (!open) {
      setConfirmationText('')
    }
  }, [open])

  const handleDelete = async () => {
    if (!isConfirmationValid) return

    try {
      await deleteProject.mutateAsync(project.id)
      toast.success('Project deleted successfully')
      onOpenChange(false)
      if (location.pathname.startsWith(`/projects/${project.id}`)) {
        navigate('/projects')
      }
    } catch {
      toast.error('Failed to delete project')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated"
        hideCloseButton
      >
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
            Delete Project
          </DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Are you sure you want to delete the project "<span className="font-bold">{project.name}</span>"?
          <br />
          The project will be archived and hidden from listings. To permanently remove it and all
          its data, use Decommission instead.
        </DialogDescription>
        <div className="space-y-2">
          <Input
            placeholder="Enter project name"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            className="w-full"
            aria-label="Type project name to confirm deletion"
          />
          <p className="text-sm text-muted-foreground">
            Type "{project.name}" to enable the Delete Project button.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmationValid || deleteProject.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DeleteIcon className="h-6 w-6" />
            {deleteProject.isPending ? 'Deleting...' : 'Delete Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
